import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import { btnSecondary, btnTransparent } from '../../styles/buttonStyles';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TagGroup {
  id: number;
  name: string;
  position: number;
  tags_count: number;
  active_publications_count: number;
}

/* ------------------------------------------------------------------ */
/*  Data fetching                                                      */
/* ------------------------------------------------------------------ */

async function fetchTagGroups(orgId: string): Promise<TagGroup[]> {
  const res = await fetch(`/organizations/${orgId}/tag_groups.json`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to fetch tag groups');
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const PageContainer = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CreateButton = styled.a`
  ${btnSecondary}
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorState = styled.div`
  padding: 20px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 14px;
`;

const RowList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
`;

const RowItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagGroupName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const TagGroupSummary = styled.span`
  font-size: 13px;
  color: #666;
`;

const EditLink = styled.a`
  ${btnTransparent}
  height: auto;
  padding: 0;
`;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function buildSummary(tagsCount: number, pubsCount: number): string {
  if (tagsCount === 0) return 'No tags';
  const tagPart = `${tagsCount} tag${tagsCount === 1 ? '' : 's'}`;
  if (pubsCount === 0) return `${tagPart} - Disabled on all publications`;
  const pubPart = `${pubsCount} publication${pubsCount === 1 ? '' : 's'}`;
  return `${tagPart} on ${pubPart}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function OrgTagGroupsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  useDocumentTitle('Tags : Contently');

  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTagGroups(orgId);
      setTagGroups(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load tag groups');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!orgId) {
    return (
      <PageContainer>
        <ErrorState>Organization ID is required</ErrorState>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Loading tag groups...</LoadingState>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState>
          <h3>Error loading tag groups</h3>
          <p>{error}</p>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Title>Tags</Title>
        <CreateButton href={`/organizations/${orgId}/tag_groups/new`}>
          Create Tags
        </CreateButton>
      </PageHeader>

      {tagGroups.length === 0 ? (
        <EmptyState>No tag groups have been created yet.</EmptyState>
      ) : (
        <RowList>
          {tagGroups.map((tg) => (
            <RowItem key={tg.id}>
              <RowLeft>
                <TagGroupName>{tg.name}</TagGroupName>
                <TagGroupSummary>
                  {buildSummary(tg.tags_count, tg.active_publications_count)}
                </TagGroupSummary>
              </RowLeft>
              <EditLink href={`/organizations/${orgId}/tag_groups/${tg.id}/edit`}>
                Edit
              </EditLink>
            </RowItem>
          ))}
        </RowList>
      )}
    </PageContainer>
  );
}
